/**
 * Thin sessionStorage cache with TTL.
 * All values are JSON-serialised. Errors are silently swallowed so callers
 * never need to guard against storage quota or private-browsing restrictions.
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  value: T;
  fetchedAt: number;
}

export const sessionCache = {
  get<T>(key: string, ttlMs = DEFAULT_TTL_MS): T | null {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const entry: CacheEntry<T> = JSON.parse(raw);
      if (Date.now() - entry.fetchedAt > ttlMs) {
        sessionStorage.removeItem(key);
        return null;
      }
      return entry.value;
    } catch {
      return null;
    }
  },

  set<T>(key: string, value: T): void {
    try {
      const entry: CacheEntry<T> = { value, fetchedAt: Date.now() };
      sessionStorage.setItem(key, JSON.stringify(entry));
    } catch {
      // Quota exceeded or storage unavailable — silently skip
    }
  },

  remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};
